import React, {
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Theme,
  CircularProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { EngagementCampaignFilterProvider } from "../../contexts/EngagementCampaignFilterContext";
import { useQuickAccessData } from "../../hooks/useQuickAccessData";
import EnhancedQuickAccessCard, {
  type EnhancedQuickAccessCardProps,
} from "./EnhancedQuickAccessCard";
import MailerLifetimeMetrics from "./MailerLifetimeMetrics";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import SkipLink from "../../components/common/SkipLink";
import type { EngagementTimeRange } from "../../utils/engagementAnalytics";
import { clearEngagementCaches } from "../../services/outreach/engagementAnalyticsService";
import EngagementMetricsHeader from "../../components/dashboard/EngagementMetricsHeader";

const AnalyticsSummarySection = lazy(() => import("./AnalyticsSummary"));
const OutreachLifetimeMetrics = lazy(() => import("./OutreachLifetimeMetrics"));

const CardFallbackComponent: React.FC<{ minHeight?: number }> = ({
  minHeight = 320,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px dashed ${theme.palette.divider}`,
        minHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      role="status"
      aria-live="polite"
      aria-busy
    >
      <CircularProgress size={28} aria-label="Loading dashboard section" />
    </Paper>
  );
};

const CardFallback = memo(CardFallbackComponent);

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const timeRange: EngagementTimeRange = "30d"; // Fixed to 30 days
  const [refreshToken, setRefreshToken] = useState(0);
  const quickAccessHeadingId = useId();

  const isUserAdmin = useMemo(() => isAdmin(), [isAdmin]);

  // Fetch quick access card data
  const { data: quickAccessData, isLoading: quickAccessLoading } =
    useQuickAccessData();

  // Auto-refresh analytics periodically
  const refreshAnalytics = useCallback(async () => {
    try {
      clearEngagementCaches();
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const [key] = query.queryKey ?? [];
          if (typeof key !== "string") {
            return false;
          }
          return [
            "engagement",
            "ses",
            "top-performing",
            "device",
            "campaign",
          ].some((prefix) => key.startsWith(prefix));
        },
      });
      setRefreshToken((token) => token + 1);
    } catch (error) {
      console.error("Failed to refresh analytics dashboard", error);
    }
  }, [queryClient]);

  // Map icon names to actual icon components
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      email: <EmailIcon fontSize="large" aria-hidden="true" />,
      campaign: <CampaignIcon fontSize="large" aria-hidden="true" />,
      admin_panel_settings: (
        <AdminPanelSettingsIcon fontSize="large" aria-hidden="true" />
      ),
      person: <PersonIcon fontSize="large" aria-hidden="true" />,
    };
    return (
      iconMap[iconName] || <EmailIcon fontSize="large" aria-hidden="true" />
    );
  };

  // Filter cards based on user role (hide Admin if not admin)
  const filteredQuickAccessData = useMemo(() => {
    return quickAccessData.filter((item) => item.id !== "admin" || isUserAdmin);
  }, [quickAccessData, isUserAdmin]);

  // Build enhanced card props with icon components
  const enhancedCardProps = useMemo(() => {
    return filteredQuickAccessData.map((card) => ({
      ...card,
      icon: getIconComponent(card.icon),
      path: card.link, // Map 'link' to 'path' for EnhancedQuickAccessCard
    }));
  }, [filteredQuickAccessData]);

  return (
    <EngagementCampaignFilterProvider>
      {/* Skip link for keyboard accessibility */}
      <SkipLink href="#main-content" />

      <Box
        component="main"
        id="main-content"
        role="main"
        aria-label="Dashboard main content"
        sx={{ p: { xs: 2, md: 3 } }}
      >
        {/* Lifetime Metrics Section */}
        <section aria-label="Lifetime metrics overview">
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid
              item
              xs={12}
              md={6}
              data-testid="mailer-lifetime-metrics-container"
            >
              <MailerLifetimeMetrics />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              data-testid="outreach-lifetime-metrics-container"
            >
              <Suspense fallback={<CardFallback minHeight={250} />}>
                <OutreachLifetimeMetrics />
              </Suspense>
            </Grid>
          </Grid>
        </section>

        {/* Quick Access Cards Section */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box component="section" aria-labelledby={quickAccessHeadingId}>
              <Typography
                id={quickAccessHeadingId}
                variant="h5"
                component="h2"
                sx={{ mb: 2 }}
              >
                Quick Access
              </Typography>
              {quickAccessLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                  role="status"
                  aria-label="Loading quick access cards"
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              {!quickAccessLoading && (
                <Grid
                  container
                  spacing={2}
                  component="ul"
                  role="list"
                  sx={{ listStyle: "none", pl: 0 }}
                >
                  {enhancedCardProps.map((cardProps) => (
                    <Grid item xs={12} sm={6} key={cardProps.id} component="li">
                      <EnhancedQuickAccessCard
                        {...(cardProps as EnhancedQuickAccessCardProps)}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </EngagementCampaignFilterProvider>
  );
};

export default Dashboard;
